/**
 * An ```anim fence, on its way to becoming a sandboxed figure.
 *
 * The rule this file exists to enforce: a block is never mounted while the
 * answer is still streaming. Half a sketch is not a slow sketch, it is a
 * syntax error at best — and at worst a closure that runs with only some of
 * the guards its author wrote. Nothing executes until the fence has closed.
 */

import { useMemo } from "react";
import { Loader2 } from "lucide-react";
import { parseAnimSpec } from "../../shared/anim-spec.ts";
import { AnimFigure } from "../anim/AnimFigure.tsx";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert.tsx";

interface AnimBlockProps {
  readonly source: string;
  readonly index?: number;
  /** False while the answer is still streaming in. */
  readonly complete: boolean;
}

export function AnimBlock({ source, index, complete }: AnimBlockProps) {
  const result = useMemo(
    () => (complete ? parseAnimSpec(source) : null),
    [source, complete],
  );

  if (!complete || result === null) {
    return <Pending label="building animation…" />;
  }

  /**
   * A brief with no sketch yet. The animator is still writing it, and its
   * reviewer has yet to see it — that can take three rounds, which is the whole
   * reason it happens down here instead of in front of the prose.
   */
  if (result.kind === "pending") {
    return (
      <Pending
        label={
          result.spec.title
            ? `composing "${result.spec.title}"…`
            : "composing animation…"
        }
      />
    );
  }

  if (result.kind === "ready") {
    return (
      <AnimFigure
        spec={result.spec}
        {...(index !== undefined ? { index } : {})}
      />
    );
  }

  return (
    <Alert
      role="alert"
      data-anim="error"
      className="my-4 rounded-l-none border-0 border-l-[3px] border-l-warning bg-muted font-sans text-sm"
    >
      <AlertTitle className="font-semibold">
        This animation couldn&rsquo;t be built.
      </AlertTitle>
      <AlertDescription className="text-secondary-foreground">
        <p>{result.error}</p>
        <details className="w-full">
          <summary className="cursor-pointer text-muted-foreground">
            Show the raw block
          </summary>
          <pre className="mt-1 overflow-x-auto font-mono text-xs">{source}</pre>
        </details>
      </AlertDescription>
    </Alert>
  );
}

function Pending({ label }: { readonly label: string }) {
  return (
    <div
      role="status"
      data-anim="pending"
      className="my-6 flex items-center justify-center gap-2 rounded-lg border border-dashed border-border-strong bg-muted p-6 font-sans text-sm text-muted-foreground"
    >
      <Loader2 className="animate-spin motion-reduce:animate-none" />
      {label}
    </div>
  );
}
